import { useId, type KeyboardEvent, type ReactNode } from 'react';
import { cn } from '@/lib/cn';

export interface TabItem {
  id: string;
  label: ReactNode;
  icon?: ReactNode;
  badge?: ReactNode;
  disabled?: boolean;
}

export interface TabsProps {
  items: TabItem[];
  value: string;
  onChange: (id: string) => void;
  className?: string;
  children?: ReactNode;
}

export function Tabs({ items, value, onChange, className, children }: TabsProps) {
  const baseId = useId();

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const enabled = items.filter((item) => !item.disabled);
    const index = enabled.findIndex((item) => item.id === value);
    if (index === -1) return;
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      onChange(enabled[(index + 1) % enabled.length].id);
    }
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      onChange(enabled[(index - 1 + enabled.length) % enabled.length].id);
    }
  };

  return (
    <div className={className}>
      <div
        role="tablist"
        aria-orientation="horizontal"
        onKeyDown={handleKeyDown}
        className="table-scroll flex items-center gap-1 border-b border-line"
      >
        {items.map((item) => {
          const active = item.id === value;
          return (
            <button
              key={item.id}
              id={`${baseId}-tab-${item.id}`}
              role="tab"
              type="button"
              aria-selected={active}
              aria-controls={`${baseId}-panel-${item.id}`}
              tabIndex={active ? 0 : -1}
              disabled={item.disabled}
              onClick={() => onChange(item.id)}
              className={cn(
                'relative flex shrink-0 items-center gap-2 whitespace-nowrap px-3.5 py-2.5 text-sm font-medium transition',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
                active ? 'text-primary' : 'text-muted hover:text-fg',
                item.disabled && 'cursor-not-allowed opacity-50',
              )}
            >
              {item.icon}
              {item.label}
              {item.badge}
              {active && (
                <span aria-hidden className="absolute inset-x-1 -bottom-px h-0.5 rounded-full bg-primary" />
              )}
            </button>
          );
        })}
      </div>
      {children && (
        <div
          role="tabpanel"
          id={`${baseId}-panel-${value}`}
          aria-labelledby={`${baseId}-tab-${value}`}
          className="pt-4"
        >
          {children}
        </div>
      )}
    </div>
  );
}
